import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Search, ScanBarcode, X, Check, AlertCircle, FileText, User
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { salesReturnsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ══════════════════════════════════════════════════════════════════════════════
// BARCODE SCANNER MODAL COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const BarcodeScannerModal = ({ isOpen, onClose, onScan, products }) => {
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleScan = () => {
    if (!barcode.trim()) return;
    const product = products.find(p => 
      p.code?.toLowerCase() === barcode.toLowerCase() ||
      p.barcode?.toLowerCase() === barcode.toLowerCase() ||
      p.sku?.toLowerCase() === barcode.toLowerCase()
    );
    if (product) {
      onScan(product);
      setSuccess(`Added: ${product.name}`);
      setError('');
      setBarcode('');
      setTimeout(() => setSuccess(''), 2000);
      inputRef.current?.focus();
    } else {
      setError('Item not found. Try again.');
      setSuccess('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleScan(); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-backdropFadeIn">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden animate-modalSlideIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <ScanBarcode className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-800">Scan Item</h3>
              <p className="text-[12px] text-slate-500">Scan barcode or type manually</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-6">
          <div className="relative">
            <input ref={inputRef} type="text" value={barcode}
              onChange={(e) => { setBarcode(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              placeholder="Scan barcode or type and press Enter" autoFocus
            />
            <button onClick={handleScan} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Check className="w-4 h-4" />
            </button>
          </div>
          {success && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-[13px] text-emerald-700 font-medium">{success}</span>
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-[13px] text-red-700">{error}</span>
            </div>
          )}
          <p className="mt-4 text-[12px] text-slate-500 text-center">Modal stays open for rapid scanning. Press Enter after each scan.</p>
        </div>
      </div>
    </div>
  );
};

const RETURN_TYPES = [
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'refund', label: 'Refund' },
  { value: 'exchange', label: 'Exchange' },
  { value: 'stock_only', label: 'Stock Only (No Financial)' }
];

const REASONS = [
  'Defective Product', 'Wrong Item Shipped', 'Customer Changed Mind',
  'Damaged in Transit', 'Quality Issue', 'Not as Described', 'Other'
];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN CREATE SALES RETURN PAGE - Premium Zoho-Style Design
// ══════════════════════════════════════════════════════════════════════════════
const CreateSalesReturnPage = () => {
  const { businessId, returnId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const isEditing = !!returnId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const customerSearchRef = useRef(null);
  const itemSearchRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    linked_invoice_id: '',
    return_date: new Date().toISOString().split('T')[0],
    return_type: 'credit_note',
    reason_code: '',
    reason_notes: '',
    items: [],
    notes_customer: '',
    terms: ''
  });

  const [refund, setRefund] = useState({
    amount: 0,
    method: 'CASH',
    reference: ''
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (!bid) return;
    // Load deps in parallel, then fetchReturn once customers are in
    // state so selectedCustomer resolves without a stale-state race.
    (async () => {
      try {
        await Promise.all([fetchCustomers(), fetchProducts()]);
        if (isEditing) await fetchReturn();
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to load form data'));
      }
    })();
  }, [bid, returnId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchReturn = async () => {
    try {
      setLoading(true);
      const { data } = await salesReturnsAPI.get(returnId);
      // Backend now returns real column names (party_*, grand_total,
      // return_number, invoice_id) plus legacy aliases (customer_*,
      // total, return_no, linked_invoice_id). Prefer party_* with a
      // customer_* fallback so either response shape works during the
      // transition period.
      const partyId = data.party_id || data.customer_id;
      const linkedInvoiceId = data.linked_invoice_id || data.invoice_id;
      setFormData(prev => ({
        ...prev,
        ...data,
        customer_id: partyId,
        linked_invoice_id: linkedInvoiceId,
      }));
      if (partyId) await fetchInvoices(partyId);
      const cust = customers.find(c => (c.party_id || c.id) === partyId);
      if (cust) {
        setSelectedCustomer(cust);
        setCustomerSearch(cust.name);
      } else if (data.party_name || data.customer_name) {
        setCustomerSearch(data.party_name || data.customer_name);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load sales return'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/parties?type=customer');
      setCustomers(response.data.parties || response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load customers'));
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/items');
      setProducts(response.data.data || response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load items'));
    }
  };

  const fetchInvoices = async (customerId) => {
    if (!customerId) { setInvoices([]); return; }
    try {
      const response = await api.get('/invoices', { params: { customer_id: customerId } });
      setInvoices(response.data.invoices || response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load invoices'));
    }
  };

  const handleCustomerSelect = (customer) => {
    const custId = customer.party_id || customer.id;
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    setFormData(prev => ({
      ...prev,
      customer_id: custId,
      linked_invoice_id: '',
      items: [],
    }));
    fetchInvoices(custId);
  };

  const handleInvoiceLink = async (invoiceId) => {
    setFormData(prev => ({ ...prev, linked_invoice_id: invoiceId }));
    if (!invoiceId) return;

    // Sync: update customer selection from the already-loaded list cache
    const inv = invoices.find(i => i.id === invoiceId);
    if (inv) {
      const invPartyId = inv.party_id || inv.customer_id;
      const cust = customers.find(c => (c.party_id || c.id) === invPartyId);
      if (cust) handleCustomerSelect(cust);
    }

    // Async: fetch the invoice with its line items for pre-fill.
    // The list response never includes items -- salesReturnsAPI.getFromInvoice
    // pulls from sales_invoice_items and was built specifically for this path.
    try {
      const { data } = await salesReturnsAPI.getFromInvoice(invoiceId);
      if (data.items?.length) {
        const returnItems = data.items.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name || item.name,
          item_code: item.item_sku || item.item_code || item.code || '',
          quantity: item.quantity || 1,
          rate: item.rate || 0,
          tax_rate: item.tax_rate || 0,
          tax_amount: item.tax_amount || 0,
          amount: item.total_amount || item.amount || 0,
          restock_action: 'restock',
        }));
        setFormData(prev => ({ ...prev, items: returnItems }));
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not pre-fill items from invoice'));
    }
  };

  const addItem = (product = null) => {
    if (product) {
      const existingIndex = formData.items.findIndex(item => item.item_id === product.id);
      if (existingIndex >= 0) {
        updateItem(existingIndex, 'quantity', (formData.items[existingIndex].quantity || 1) + 1);
        return;
      }
      const lineTotal = 1 * (product.selling_price || product.sale_price || 0);
      const taxRate = product.tax_rate || 0;
      const taxAmt = lineTotal * (taxRate / 100);
      
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: product.id, item_name: product.name, item_code: product.code || '',
          quantity: 1, rate: product.selling_price || product.sale_price || 0,
          tax_rate: taxRate, tax_amount: Math.round(taxAmt * 100) / 100,
          amount: Math.round((lineTotal + taxAmt) * 100) / 100,
          restock_action: 'restock', condition: 'good'
        }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: '', item_name: '', item_code: '', quantity: 1,
          rate: 0, tax_rate: 0, tax_amount: 0, amount: 0,
          restock_action: 'restock', condition: 'good'
        }]
      }));
    }
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'item_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].item_name = product.name;
          newItems[index].item_code = product.code || '';
          newItems[index].rate = product.selling_price || product.sale_price || 0;
          newItems[index].tax_rate = product.tax_rate || 0;
        }
      }

      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;

      const lineTotal = qty * rate;
      const taxAmt = lineTotal * (taxRate / 100);

      newItems[index].tax_amount = Math.round(taxAmt * 100) / 100;
      newItems[index].amount = Math.round((lineTotal + taxAmt) * 100) / 100;

      return { ...prev, items: newItems };
    });
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.rate || 0)), 0);
  const taxTotal = formData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const total = subtotal + taxTotal;

  const handleSave = async (asDraft = false) => {
    if (!formData.customer_id) { toast.error('Please select a customer'); return; }
    if (formData.items.length === 0) { toast.error('Please add at least one item'); return; }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        customer_name: selectedCustomer?.name,
        subtotal, tax_total: taxTotal, total,
        status: asDraft ? 'DRAFT' : 'CONFIRMED',
        refund: formData.return_type === 'refund' ? refund : null
      };

      const response = isEditing
        ? await salesReturnsAPI.update(returnId, payload)
        : await salesReturnsAPI.create(payload);

      toast.success(asDraft ? 'Return saved as draft' : 'Return confirmed');
      const savedId = response.data?.id || response.data?.return?.id || returnId;
      navigate(`/business/${bid}/sales-returns/${savedId}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save return'));
    } finally {
      setSaving(false);
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch) ||
    c.email?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F0F2F5]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F0F2F5] animate-fadeIn">
      {/* ══════════════════════════════════════════════════════════════════════
          HEADER - Blue Title Style
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-[#F0F2F5] px-6 py-5">
        <div className="max-w-[1400px] mx-auto flex items-start justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-blue-600">
              {isEditing ? 'Edit Sales Return' : 'Create Sales Return'}
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">Record customer return and process refund</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/business/${bid}/sales-returns`)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-600 hover:text-slate-800 transition-colors text-[13px] font-medium">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => handleSave(true)} disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-[13px] font-medium shadow-sm">
              <FileText className="w-4 h-4" /> Save Draft
            </button>
            <button onClick={() => handleSave(false)} disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-[13px] font-medium shadow-sm">
              <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Confirm Return'}
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-8 space-y-5">
              
              {/* CUSTOMER DETAILS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Customer Details</h2>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative" ref={customerSearchRef}>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Search Customer</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text" value={customerSearch}
                        onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
                        placeholder="Search by name, mobile, email..."
                      />
                    </div>
                    
                    {showCustomerDropdown && filteredCustomers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredCustomers.slice(0, 8).map(customer => {
                          const contact = [customer.mobile, customer.email].filter(Boolean).join(' • ');
                          return (
                            <div key={customer.party_id || customer.id}
                              onClick={() => handleCustomerSelect(customer)}
                              className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-[13px] font-medium text-slate-800 truncate">{customer.name}</p>
                                {contact && <p className="text-[11px] text-slate-500 shrink-0 truncate">{contact}</p>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Link to Invoice</label>
                    <select
                      value={formData.linked_invoice_id}
                      onChange={(e) => handleInvoiceLink(e.target.value)}
                      disabled={!formData.customer_id}
                      className={`w-full px-4 py-3 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none ${!formData.customer_id ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white'}`}
                    >
                      <option value="">{formData.customer_id ? 'Select invoice (optional)' : 'Select a customer first'}</option>
                      {invoices.map(inv => (
                        <option key={inv.id} value={inv.id}>
                          {inv.invoice_number} - {inv.customer_name || inv.party_name} ({'\u20B9'}{Number(inv.grand_total || 0).toLocaleString('en-IN')})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedCustomer && (() => {
                  const selContact = [selectedCustomer.mobile, selectedCustomer.email].filter(Boolean).join(' • ');
                  return (
                    <div className="mt-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 truncate">{selectedCustomer.name}</p>
                          {selContact && <p className="text-[11px] text-slate-500 truncate">{selContact}</p>}
                        </div>
                        <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setFormData(prev => ({ ...prev, customer_id: '' })); }}
                          className="text-slate-400 hover:text-slate-600 shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ITEMS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Return Items</h2>
                  <span className="text-[12px] text-slate-400 ml-1">{formData.items.length} items added</span>
                </div>

                {/* Quick Add Row */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 relative" ref={itemSearchRef}>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={itemSearch}
                      onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                      onFocus={() => setShowItemDropdown(true)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
                      placeholder="Quick add item..."
                    />
                    {showItemDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {filteredProducts.slice(0, 8).map(product => (
                          <div key={product.id} onClick={() => addItem(product)}
                            className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[13px] font-medium text-slate-800 truncate">{product.name}</p>
                              {product.code && <p className="text-[11px] text-slate-500 shrink-0">{product.code}</p>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > 8 && (
                          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                            Showing 8 of {filteredProducts.length} — type to narrow
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setShowScanner(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all text-[12px] font-medium">
                    <ScanBarcode className="w-4 h-4" /> Scan
                  </button>
                  <button onClick={() => addItem()}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all text-[12px] font-medium">
                    <Plus className="w-4 h-4" /> Add Line
                  </button>
                </div>

                {/* Items Table */}
                {formData.items.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-slate-200 rounded-lg bg-slate-50/30">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-[13px] text-slate-500">No items added yet</p>
                    <p className="text-[12px] text-slate-400 mt-1">Click &quot;Add Line&quot; or search for items to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">#</th>
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">Item</th>
                          <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-slate-500 uppercase">Qty</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Rate</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Tax %</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Amount</th>
                          <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-slate-500 uppercase">Restock</th>
                          <th className="px-3 py-2.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="px-3 py-2.5 text-[12px] text-slate-500">{index + 1}</td>
                            <td className="px-3 py-2.5">
                              <select value={item.item_id} onChange={(e) => updateItem(index, 'item_id', e.target.value)}
                                className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] focus:outline-none focus:border-blue-500">
                                <option value="">Select Item</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" min="0" />
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="number" value={item.rate} onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-24 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:border-blue-500" min="0" />
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:border-blue-500" min="0" placeholder="%" />
                            </td>
                            <td className="px-3 py-2.5 text-right text-[13px] font-medium text-slate-700">
                              ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2.5">
                              <select value={item.restock_action} onChange={(e) => updateItem(index, 'restock_action', e.target.value)}
                                className="w-24 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] focus:outline-none focus:border-blue-500">
                                <option value="restock">Restock</option>
                                <option value="writeoff">Write-off</option>
                                <option value="damaged">Damaged</option>
                              </select>
                            </td>
                            <td className="px-3 py-2.5">
                              <button onClick={() => removeItem(index)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* NOTES Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Notes & Reason</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Reason for Return</label>
                    <select value={formData.reason_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason_code: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none">
                      <option value="">Select reason</option>
                      {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Additional Notes</label>
                    <textarea value={formData.reason_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, reason_notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none"
                      rows={2} placeholder="Add notes about the return..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-4 space-y-5">
              
              {/* RETURN DETAILS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Return Details</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Return Date</label>
                    <input type="date" value={formData.return_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, return_date: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Return Type</label>
                    <select value={formData.return_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, return_type: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500">
                      {RETURN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* SUMMARY Card - Yellow Background */}
              <div className="bg-amber-50 rounded-xl p-5 shadow-sm border border-amber-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-amber-700" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600">+ ₹{taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-amber-200 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-800">Return Total</span>
                      <span className="text-[18px] font-bold text-emerald-600">
                        ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* REFUND DETAILS Card (only for refund type) */}
              {formData.return_type === 'refund' && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60 animate-fadeInUp">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Refund Details</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Refund Amount</label>
                      <input type="number" value={refund.amount || total}
                        onChange={(e) => setRefund(r => ({ ...r, amount: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Refund Method</label>
                      <select value={refund.method}
                        onChange={(e) => setRefund(r => ({ ...r, method: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500">
                        <option value="CASH">Cash</option>
                        <option value="UPI">UPI</option>
                        <option value="CARD">Card</option>
                        <option value="BANK">Bank Transfer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Reference</label>
                      <input type="text" value={refund.reference}
                        onChange={(e) => setRefund(r => ({ ...r, reference: e.target.value }))}
                        placeholder="Transaction ID, etc."
                        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* EXCHANGE DETAILS Card (only for exchange type) */}
              {formData.return_type === 'exchange' && (
                <div className="bg-purple-50 rounded-xl p-5 shadow-sm border border-purple-200/60 animate-fadeInUp">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Exchange Details</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-3 bg-white rounded-lg border border-purple-200">
                      <p className="text-[12px] text-purple-700 font-medium mb-1">How Exchange Works</p>
                      <p className="text-[11px] text-slate-500">
                        Customer returns items and receives replacement items. Any price difference will be adjusted.
                      </p>
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Exchange Items</label>
                      <p className="text-[11px] text-slate-400 mb-2">Add the new items customer will receive in exchange</p>
                      <div className="space-y-2">
                        {(formData.exchange_items || []).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded border border-purple-100">
                            <span className="flex-1 text-[12px] text-slate-700">{item.item_name || 'Select item'}</span>
                            <span className="text-[12px] text-slate-500">Qty: {item.quantity}</span>
                            <button onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                exchange_items: (prev.exchange_items || []).filter((_, i) => i !== idx)
                              }));
                            }} className="p-1 text-red-400 hover:text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              exchange_items: [...(prev.exchange_items || []), { item_id: '', item_name: '', quantity: 1, rate: 0 }]
                            }));
                          }}
                          className="w-full py-2 border-2 border-dashed border-purple-200 rounded-lg text-[12px] text-purple-600 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                        >
                          + Add Exchange Item
                        </button>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-purple-200">
                      <div className="flex justify-between text-[12px]">
                        <span className="text-slate-600">Return Value</span>
                        <span className="text-slate-700">₹{total.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-[12px] mt-1">
                        <span className="text-slate-600">Exchange Value</span>
                        <span className="text-slate-700">₹{((formData.exchange_items || []).reduce((s, i) => s + (i.rate * i.quantity), 0)).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-[13px] font-medium mt-2 pt-2 border-t border-purple-200">
                        <span className="text-slate-700">Difference</span>
                        <span className={`${(total - (formData.exchange_items || []).reduce((s, i) => s + (i.rate * i.quantity), 0)) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{Math.abs(total - (formData.exchange_items || []).reduce((s, i) => s + (i.rate * i.quantity), 0)).toLocaleString('en-IN')}
                          {(total - (formData.exchange_items || []).reduce((s, i) => s + (i.rate * i.quantity), 0)) >= 0 ? ' to customer' : ' from customer'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Badge */}
              <div className="text-center pt-2">
                <span className="inline-flex items-center px-4 py-1.5 bg-slate-100 text-slate-600 text-[11px] font-semibold rounded-full uppercase tracking-wider">
                  Draft
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal isOpen={showScanner} onClose={() => setShowScanner(false)}
        onScan={(product) => addItem(product)} products={products}
      />
    </div>
  );
};

export default CreateSalesReturnPage;
